import { useEffect, useState, useRef, useCallback, lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { PermissionsProvider } from './context/PermissionsContext';
import { SettingsProvider } from './context/SettingsContext';
import { BranchProvider } from './context/BranchContext';
import { ConfirmDialogProvider } from './context/ConfirmDialogContext';
import { I18nProvider } from './i18n';
import { Toaster } from './components/ui/sonner';
import { toast } from 'sonner';
import config from './config';
import './App.css';
import './print.css';

import Login from './pages/Login';
import LandingPage from './pages/LandingPage';
import ModuleHome from './pages/ModuleHome';
import Layout from './components/Layout';
import HRLayout from './components/HRLayout';
import ERPNextShell from './components/erpnext/Shell';
import ErrorBoundary from './components/ErrorBoundary';
import LoadingBar from './components/LoadingBar';

const Register = lazy(() => import('./pages/Register'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const Inventory = lazy(() => import('./pages/Inventory'));
const Suppliers = lazy(() => import('./pages/Suppliers'));
const Recipes = lazy(() => import('./pages/Recipes'));
const Production = lazy(() => import('./pages/Production'));
const Reports = lazy(() => import('./pages/Reports'));
const ComprehensiveReport = lazy(() => import('./pages/ComprehensiveReport'));
const PurchaseOrders = lazy(() => import('./pages/PurchaseOrders'));
const Receiving = lazy(() => import('./pages/Receiving'));
const UserManagement = lazy(() => import('./pages/UserManagement'));
const UserProfile = lazy(() => import('./pages/UserProfile'));
const AcceptInvitation = lazy(() => import('./pages/AcceptInvitation'));
const Branches = lazy(() => import('./pages/Branches'));
const StockTransfers = lazy(() => import('./pages/StockTransfers'));
const BranchComparison = lazy(() => import('./pages/BranchComparison'));
const NotificationsPage = lazy(() => import('./pages/NotificationsPage'));
const AppSettings = lazy(() => import('./pages/AppSettings'));

const HRDashboard = lazy(() => import('./pages/HRDashboard'));
const HREmployees = lazy(() => import('./pages/HREmployees'));
const HRDepartments = lazy(() => import('./pages/HRDepartments'));
const HRPerformance = lazy(() => import('./pages/HRPerformance'));
const HRRecruitment = lazy(() => import('./pages/HRRecruitment'));
const HRAttendance = lazy(() => import('./pages/HRAttendance'));
const HRAttendanceReports = lazy(() => import('./pages/HRAttendanceReports'));
const EmployeeRegister = lazy(() => import('./pages/EmployeeRegister'));
const OrgChart = lazy(() => import('./pages/OrgChart'));
const TimeAttendance = lazy(() => import('./pages/TimeAttendance'));
const JobPostings = lazy(() => import('./pages/JobPostings'));
const LeaveManagement = lazy(() => import('./pages/LeaveManagement'));
const PerformanceManagement = lazy(() => import('./pages/PerformanceManagement'));
const LearningDevelopment = lazy(() => import('./pages/LearningDevelopment'));
const EmployeeEngagement = lazy(() => import('./pages/EmployeeEngagement'));
const Compliance = lazy(() => import('./pages/Compliance'));
const AIInsights = lazy(() => import('./pages/AIInsights'));
const HRSettings = lazy(() => import('./pages/HRSettings'));
const HRCoreMasterData = lazy(() => import('./pages/HRCoreMasterData'));
const HRComprehensiveReport = lazy(() => import('./pages/HRComprehensiveReport'));
const AuditTrail = lazy(() => import('./pages/AuditTrail'));
const BulkOperations = lazy(() => import('./pages/BulkOperations'));
const PayrollApproval = lazy(() => import('./pages/PayrollApproval'));
const EmployeeOnboarding = lazy(() => import('./pages/EmployeeOnboarding'));
const EmployeeInvitations = lazy(() => import('./pages/EmployeeInvitations'));
const EmployeeProfile = lazy(() => import('./pages/EmployeeProfile'));
const SuperAdminDashboard = lazy(() => import('./pages/SuperAdminDashboard'));
const Payroll2026 = lazy(() => import('./pages/Payroll2026'));
const MyPayslips = lazy(() => import('./pages/MyPayslips'));
const HRDiscipline = lazy(() => import('./pages/HRDiscipline'));
const DisciplineGrievance = lazy(() => import('./pages/DisciplineGrievance'));
const HRDocumentManagement = lazy(() => import('./pages/HRDocumentManagement'));
const MyDocuments = lazy(() => import('./pages/MyDocuments'));
const HRAnalyticsDashboard = lazy(() => import('./pages/HRAnalyticsDashboard'));

const ProductionDashboard = lazy(() => import('./pages/ProductionDashboard'));
const ProductionRequisitions = lazy(() => import('./pages/ProductionRequisitions'));
const ProductionPlanning = lazy(() => import('./pages/ProductionPlanning'));
const QualityControl = lazy(() => import('./pages/QualityControl'));

const FinanceModule = lazy(() => import('./pages/FinanceModule'));
const FinanceChartOfAccounts = lazy(() => import('./pages/FinanceChartOfAccounts'));
const FinanceJournals = lazy(() => import('./pages/FinanceJournals'));
const FinanceBudgets = lazy(() => import('./pages/FinanceBudgets'));
const FinanceDashboard = lazy(() => import('./pages/finance/FinanceDashboard'));
const FinanceInvoices = lazy(() => import('./pages/finance/Invoices'));
const FinanceBills = lazy(() => import('./pages/finance/Bills'));
const FinanceBanking = lazy(() => import('./pages/finance/Banking'));
const FinanceReports = lazy(() => import('./pages/finance/FinanceReports'));
const FinanceExpenses = lazy(() => import('./pages/finance/Expenses'));
const SalesModule = lazy(() => import('./pages/SalesModule'));
const MasterDataModule = lazy(() => import('./pages/MasterDataModule'));
const SystemAdmin = lazy(() => import('./pages/SystemAdmin'));
const DataImportExport = lazy(() => import('./pages/DataImportExport'));
const DocTypeListPage = lazy(() => import('./pages/DocTypeListPage'));
const DocTypeFormPage = lazy(() => import('./pages/DocTypeFormPage'));
const InboxPage = lazy(() => import('./pages/InboxPage'));
const ERPDashboardsPage = lazy(() => import('./pages/ERPDashboardsPage'));
const TodoPage = lazy(() => import('./pages/TodoPage'));
const DocTypeBuilderPage = lazy(() => import('./pages/DocTypeBuilderPage'));
const ReportBuilderPage = lazy(() => import('./pages/ReportBuilderPage'));
const IndustryTemplatesPage = lazy(() => import('./pages/IndustryTemplatesPage'));
const AdminConsolePage = lazy(() => import('./pages/AdminConsolePage'));

// Advanced ERP Modules
const CustomerManagement = lazy(() => import('./pages/CustomerManagement'));
const AdvancedInventory = lazy(() => import('./pages/AdvancedInventory'));
const POSManagement = lazy(() => import('./pages/POSManagement'));
const ProcurementHub = lazy(() => import('./pages/ProcurementHub'));
const FinanceAdvanced = lazy(() => import('./pages/FinanceAdvanced'));
const WarehouseLogistics = lazy(() => import('./pages/WarehouseLogistics'));
const AdvancedAnalytics = lazy(() => import('./pages/AdvancedAnalytics'));
const ProductionAdvanced = lazy(() => import('./pages/ProductionAdvanced'));
const AssetManagement = lazy(() => import('./pages/AssetManagement'));
const StoreOperations = lazy(() => import('./pages/StoreOperations'));

const SalesAdvanced = lazy(() => import('./pages/SalesAdvanced'));
const SalesPOSImprovements = lazy(() => import('./pages/SalesPOSImprovements'));
const SalesReports = lazy(() => import('./pages/SalesReports'));
const InventoryAdvanced = lazy(() => import('./pages/InventoryAdvanced'));
const ComplianceDashboard = lazy(() => import('./pages/ComplianceDashboard'));
const HRAdvanced = lazy(() => import('./pages/HRAdvanced'));
const InventoryImprovements = lazy(() => import('./pages/InventoryImprovements'));
const InventoryReports = lazy(() => import('./pages/InventoryReports'));
const ProductionImprovements = lazy(() => import('./pages/ProductionImprovements'));
const HRImprovements = lazy(() => import('./pages/HRImprovements'));
const FinanceImprovements = lazy(() => import('./pages/FinanceImprovements'));
const BranchImprovements = lazy(() => import('./pages/BranchImprovements'));
const SystemImprovements = lazy(() => import('./pages/SystemImprovements'));

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
export const API = `${BACKEND_URL}/api`;

let lastGlobalToastTime = {};
const GLOBAL_TOAST_COOLDOWN = 3000;
function shouldShowGlobalToast(key) {
  const now = Date.now();
  if (lastGlobalToastTime[key] && now - lastGlobalToastTime[key] < GLOBAL_TOAST_COOLDOWN) return false;
  lastGlobalToastTime[key] = now;
  return true;
}

axios.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error?.response?.status;
    const isLoginPage = window.location.pathname === '/login';
    const isAuthMeRequest = error?.config?.url?.includes('/auth/me');

    if (status === 401 && !isLoginPage && !isAuthMeRequest) {
      localStorage.removeItem('token');
      delete axios.defaults.headers.common['Authorization'];
      if (shouldShowGlobalToast('auth_expired')) {
        toast.error('Session expired. Please log in again.');
      }
      window.location.href = '/login';
      return Promise.reject(error);
    }

    if (status === 403 && shouldShowGlobalToast('permission_denied')) {
      toast.error('You do not have permission to perform this action.');
    }

    if (status >= 500 && shouldShowGlobalToast('server_error')) {
      toast.error('A server error occurred. Please try again later.');
    }

    if (!error.response && error.code === 'ERR_NETWORK' && shouldShowGlobalToast('network_error')) {
      toast.error('Network error. Please check your internet connection.');
    }

    return Promise.reject(error);
  }
);

const AppLayout = ({ children, user, setUser }) => {
  // ERPNext-style shell. Set localStorage.erp_layout='legacy' to revert to
  // the previous Layout temporarily during the migration.
  const useLegacy = typeof window !== 'undefined' && localStorage.getItem('erp_layout') === 'legacy';
  if (useLegacy) {
    return <Layout user={user} setUser={setUser}>{children}</Layout>;
  }
  return <ERPNextShell user={user} setUser={setUser}>{children}</ERPNextShell>;
};

const HRAppLayout = ({ children, user, setUser }) => {
  return <HRLayout user={user} setUser={setUser}>{children}</HRLayout>;
};

const NotFoundRedirect = () => {
  const nav = useNavigate();
  useEffect(() => {
    toast.info('Page not found — redirecting to dashboard', { duration: 3000 });
    nav('/', { replace: true });
  }, [nav]);
  return null;
};

const PageLoader = () => (
  <div className="flex items-center justify-center min-h-[60vh]">
    <div className="flex flex-col items-center space-y-4">
      <div className="w-12 h-12 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin" />
      <p className="text-slate-500 font-medium">Loading...</p>
    </div>
  </div>
);

const SESSION_TIMEOUT_MS = 30 * 60 * 1000;
const SESSION_WARNING_MS = 5 * 60 * 1000;

function SessionTimeoutManager({ user, setUser }) {
  const [showWarning, setShowWarning] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const lastActivityRef = useRef(Date.now());
  const warningTimerRef = useRef(null);
  const logoutTimerRef = useRef(null);
  const countdownRef = useRef(null);

  const resetTimers = useCallback(() => {
    lastActivityRef.current = Date.now();
    setShowWarning(false);
    if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
    if (logoutTimerRef.current) clearTimeout(logoutTimerRef.current);
    if (countdownRef.current) clearInterval(countdownRef.current);

    warningTimerRef.current = setTimeout(() => {
      setShowWarning(true);
      setCountdown(Math.floor(SESSION_WARNING_MS / 1000));
      countdownRef.current = setInterval(() => {
        setCountdown(prev => {
          if (prev <= 1) {
            clearInterval(countdownRef.current);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }, SESSION_TIMEOUT_MS - SESSION_WARNING_MS);

    logoutTimerRef.current = setTimeout(() => {
      localStorage.removeItem('token');
      delete axios.defaults.headers.common['Authorization'];
      setUser(null);
      toast.error('Session expired due to inactivity. Please log in again.');
      window.location.href = '/login';
    }, SESSION_TIMEOUT_MS);
  }, [setUser]);

  useEffect(() => {
    if (!user) return;
    const events = ['mousedown', 'keydown', 'scroll', 'touchstart'];
    const handleActivity = () => {
      if (!showWarning) resetTimers();
    };
    events.forEach(e => window.addEventListener(e, handleActivity));
    resetTimers();
    return () => {
      events.forEach(e => window.removeEventListener(e, handleActivity));
      if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
      if (logoutTimerRef.current) clearTimeout(logoutTimerRef.current);
      if (countdownRef.current) clearInterval(countdownRef.current);
    };
  }, [user, resetTimers, showWarning]);

  const handleContinue = () => {
    setShowWarning(false);
    resetTimers();
  };

  if (!showWarning || !user) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[9999]">
      <div className="bg-white rounded-2xl p-8 max-w-md w-full mx-4 shadow-2xl">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center">
            <svg className="w-6 h-6 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <div>
            <h3 className="text-lg font-bold text-slate-900">Session Expiring</h3>
            <p className="text-sm text-slate-500">Your session will expire due to inactivity</p>
          </div>
        </div>
        <p className="text-slate-600 mb-6">
          You will be logged out in <span className="font-bold text-amber-600">{Math.floor(countdown / 60)}:{String(countdown % 60).padStart(2, '0')}</span>.
          Click below to continue your session.
        </p>
        <div className="flex gap-3">
          <button
            onClick={handleContinue}
            className="flex-1 bg-emerald-600 text-white py-2.5 px-4 rounded-xl font-semibold hover:bg-emerald-700 transition-colors"
          >
            Continue Session
          </button>
          <button
            onClick={() => {
              localStorage.removeItem('token');
              delete axios.defaults.headers.common['Authorization'];
              setUser(null);
              window.location.href = '/login';
            }}
            className="px-4 py-2.5 rounded-xl border border-slate-200 text-slate-600 hover:bg-slate-50 transition-colors"
          >
            Log Out
          </button>
        </div>
      </div>
    </div>
  );
}

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isSetup, setIsSetup] = useState(null);

  useEffect(() => {
    const init = async () => {
      try {
        const setupRes = await axios.get(`${API}/auth/setup-status`);
        setIsSetup(setupRes.data.is_setup);

        if (setupRes.data.is_setup) {
          const token = localStorage.getItem('token');
          if (token) {
            axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
            try {
              const res = await axios.get(`${API}/auth/me`);
              setUser(res.data);
            } catch {
              localStorage.removeItem('token');
              delete axios.defaults.headers.common['Authorization'];
            }
          }
        }
      } catch {
        setIsSetup(true);
        const token = localStorage.getItem('token');
        if (token) {
          axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
          try {
            const res = await axios.get(`${API}/auth/me`);
            setUser(res.data);
          } catch {
            localStorage.removeItem('token');
            delete axios.defaults.headers.common['Authorization'];
          }
        }
      } finally {
        setLoading(false);
      }
    };
    init();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="flex flex-col items-center space-y-6">
          <div className="w-20 h-20 bg-emerald-600 rounded-2xl flex items-center justify-center shadow-lg">
            <span className="text-3xl font-bold text-white">FM</span>
          </div>
          <div className="flex flex-col items-center space-y-3">
            <div className="w-10 h-10 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin" />
            <p className="text-slate-600 font-semibold text-lg">{config.APP_NAME}</p>
            <p className="text-slate-400 text-sm">Loading your workspace...</p>
          </div>
        </div>
      </div>
    );
  }

  const PrivateRoute = ({ children }) => {
    if (!user) return <Navigate to="/login" replace />;
    return children;
  };

  return (
    <BrowserRouter>
      <I18nProvider>
      <ConfirmDialogProvider>
      <PermissionsProvider user={user}>
        <SettingsProvider>
          <BranchProvider user={user}>
            <SessionTimeoutManager user={user} setUser={setUser} />
            <div role="status" aria-live="polite">
              <Toaster 
                position="top-right" 
                richColors 
                closeButton
                visibleToasts={3}
                duration={4000}
                toastOptions={{
                  style: { fontSize: '14px' },
                  classNames: {
                    error: 'toast-error-duration',
                  },
                }}
              />
            </div>
            <Suspense fallback={<><LoadingBar /><PageLoader /></>}>
              <ErrorBoundary>
                <Routes>
                <Route path="/register" element={user ? <Navigate to="/" replace /> : isSetup === true ? <Navigate to="/login" replace /> : <Register setUser={setUser} setIsSetup={setIsSetup} />} />
                <Route path="/login" element={user ? <Navigate to="/" replace /> : isSetup === false ? <Navigate to="/register" replace /> : <Login setUser={setUser} />} />
                <Route path="/accept-invitation/:token" element={<AcceptInvitation setUser={setUser} />} />
                <Route path="/employee-register" element={<EmployeeRegister />} />
                <Route path="/hr/employee-register/:token" element={<EmployeeRegister />} />

                <Route path="/" element={
                  user ? (
                    <AppLayout user={user} setUser={setUser}>
                      <ModuleHome />
                    </AppLayout>
                  ) : isSetup === false ? (
                    <Navigate to="/register" replace />
                  ) : (
                    <LandingPage />
                  )
                } />
                <Route path="/dashboard" element={
                  <PrivateRoute>
                    <AppLayout user={user} setUser={setUser}>
                      <Dashboard />
                    </AppLayout>
                  </PrivateRoute>
                } />

                <Route path="/inventory" element={<PrivateRoute><AppLayout user={user} setUser={setUser}><Inventory /></AppLayout></PrivateRoute>} />
                <Route path="/suppliers" element={<PrivateRoute><AppLayout user={user} setUser={setUser}><Suppliers /></AppLayout></PrivateRoute>} />
                <Route path="/recipes" element={<PrivateRoute><AppLayout user={user} setUser={setUser}><Recipes /></AppLayout></PrivateRoute>} />
                <Route path="/purchase-orders" element={<PrivateRoute><AppLayout user={user} setUser={setUser}><PurchaseOrders /></AppLayout></PrivateRoute>} />
                <Route path="/orders" element={<Navigate to="/purchase-orders" replace />} />
                <Route path="/receiving" element={<PrivateRoute><AppLayout user={user} setUser={setUser}><Receiving /></AppLayout></PrivateRoute>} />
                <Route path="/stock-transfers" element={<PrivateRoute><AppLayout user={user} setUser={setUser}><StockTransfers /></AppLayout></PrivateRoute>} />

                <Route path="/production" element={<PrivateRoute><AppLayout user={user} setUser={setUser}><Production /></AppLayout></PrivateRoute>} />
                <Route path="/production/dashboard" element={<PrivateRoute><AppLayout user={user} setUser={setUser}><ProductionDashboard /></AppLayout></PrivateRoute>} />
                <Route path="/production/requisitions" element={<PrivateRoute><AppLayout user={user} setUser={setUser}><ProductionRequisitions /></AppLayout></PrivateRoute>} />
                <Route path="/production/planning" element={<PrivateRoute><AppLayout user={user} setUser={setUser}><ProductionPlanning /></AppLayout></PrivateRoute>} />
                <Route path="/production/quality" element={<PrivateRoute><AppLayout user={user} setUser={setUser}><QualityControl /></AppLayout></PrivateRoute>} />

                <Route path="/reports" element={<PrivateRoute><AppLayout user={user} setUser={setUser}><Reports /></AppLayout></PrivateRoute>} />
                <Route path="/reports/comprehensive" element={<PrivateRoute><AppLayout user={user} setUser={setUser}><ComprehensiveReport /></AppLayout></PrivateRoute>} />
                <Route path="/analytics" element={<Navigate to="/reports" replace />} />

                <Route path="/branches" element={<PrivateRoute><AppLayout user={user} setUser={setUser}><Branches /></AppLayout></PrivateRoute>} />
                <Route path="/branch-comparison" element={<PrivateRoute><AppLayout user={user} setUser={setUser}><BranchComparison /></AppLayout></PrivateRoute>} />
                <Route path="/users" element={<PrivateRoute><AppLayout user={user} setUser={setUser}><UserManagement /></AppLayout></PrivateRoute>} />
                <Route path="/profile" element={<PrivateRoute><AppLayout user={user} setUser={setUser}><UserProfile user={user} setUser={setUser} /></AppLayout></PrivateRoute>} />
                <Route path="/notifications" element={<PrivateRoute><AppLayout user={user} setUser={setUser}><NotificationsPage /></AppLayout></PrivateRoute>} />
                <Route path="/settings" element={<PrivateRoute><AppLayout user={user} setUser={setUser}><AppSettings /></AppLayout></PrivateRoute>} />

                <Route path="/finance" element={<PrivateRoute><AppLayout user={user} setUser={setUser}><FinanceModule /></AppLayout></PrivateRoute>}>
                  <Route index element={<FinanceDashboard />} />
                  <Route path="dashboard" element={<FinanceDashboard />} />
                  <Route path="invoices" element={<FinanceInvoices />} />
                  <Route path="bills" element={<FinanceBills />} />
                  <Route path="expenses" element={<FinanceExpenses />} />
                  <Route path="banking" element={<FinanceBanking />} />
                  <Route path="chart-of-accounts" element={<FinanceChartOfAccounts />} />
                  <Route path="journals" element={<FinanceJournals />} />
                  <Route path="reports" element={<FinanceReports />} />
                  <Route path="budgets" element={<FinanceBudgets />} />
                </Route>
                <Route path="/sales" element={<PrivateRoute><AppLayout user={user} setUser={setUser}><SalesModule /></AppLayout></PrivateRoute>} />
                <Route path="/master-data" element={<PrivateRoute><AppLayout user={user} setUser={setUser}><MasterDataModule /></AppLayout></PrivateRoute>} />
                <Route path="/system" element={<PrivateRoute><AppLayout user={user} setUser={setUser}><SystemAdmin /></AppLayout></PrivateRoute>} />
                <Route path="/data-import-export" element={<PrivateRoute><AppLayout user={user} setUser={setUser}><DataImportExport /></AppLayout></PrivateRoute>} />
                <Route path="/doctype/:name" element={<PrivateRoute><AppLayout user={user} setUser={setUser}><DocTypeListPage /></AppLayout></PrivateRoute>} />
                <Route path="/doctype/:name/:id" element={<PrivateRoute><AppLayout user={user} setUser={setUser}><DocTypeFormPage /></AppLayout></PrivateRoute>} />
                <Route path="/inbox" element={<PrivateRoute><AppLayout user={user} setUser={setUser}><InboxPage /></AppLayout></PrivateRoute>} />
                <Route path="/erp-dashboards" element={<PrivateRoute><AppLayout user={user} setUser={setUser}><ERPDashboardsPage /></AppLayout></PrivateRoute>} />
                <Route path="/erp-dashboards/:id" element={<PrivateRoute><AppLayout user={user} setUser={setUser}><ERPDashboardsPage /></AppLayout></PrivateRoute>} />
                <Route path="/todos" element={<PrivateRoute><AppLayout user={user} setUser={setUser}><TodoPage /></AppLayout></PrivateRoute>} />
                <Route path="/doctype-builder" element={<PrivateRoute><AppLayout user={user} setUser={setUser}><DocTypeBuilderPage /></AppLayout></PrivateRoute>} />
                <Route path="/report-builder" element={<PrivateRoute><AppLayout user={user} setUser={setUser}><ReportBuilderPage /></AppLayout></PrivateRoute>} />
                <Route path="/industry-templates" element={<PrivateRoute><AppLayout user={user} setUser={setUser}><IndustryTemplatesPage /></AppLayout></PrivateRoute>} />
                <Route path="/admin-console" element={<PrivateRoute><AppLayout user={user} setUser={setUser}><AdminConsolePage /></AppLayout></PrivateRoute>} />
                <Route path="/sales-advanced" element={<PrivateRoute><AppLayout user={user} setUser={setUser}><SalesAdvanced /></AppLayout></PrivateRoute>} />
                <Route path="/pos-improvements" element={<PrivateRoute><AppLayout user={user} setUser={setUser}><SalesPOSImprovements /></AppLayout></PrivateRoute>} />
                <Route path="/sales/reports" element={<PrivateRoute><AppLayout user={user} setUser={setUser}><SalesReports /></AppLayout></PrivateRoute>} />
                <Route path="/inventory-advanced" element={<PrivateRoute><AppLayout user={user} setUser={setUser}><InventoryAdvanced /></AppLayout></PrivateRoute>} />
                <Route path="/production-advanced" element={<PrivateRoute><AppLayout user={user} setUser={setUser}><ProductionAdvanced /></AppLayout></PrivateRoute>} />
                <Route path="/executive-dashboard" element={<Navigate to="/dashboard" replace />} />
                <Route path="/compliance" element={<PrivateRoute><AppLayout user={user} setUser={setUser}><ComplianceDashboard /></AppLayout></PrivateRoute>} />

                <Route path="/hr-advanced" element={<PrivateRoute><AppLayout user={user} setUser={setUser}><HRAdvanced /></AppLayout></PrivateRoute>} />
                <Route path="/inventory-improvements" element={<PrivateRoute><AppLayout user={user} setUser={setUser}><InventoryImprovements /></AppLayout></PrivateRoute>} />
                <Route path="/inventory-reports" element={<PrivateRoute><AppLayout user={user} setUser={setUser}><InventoryReports /></AppLayout></PrivateRoute>} />
                <Route path="/production-improvements" element={<PrivateRoute><AppLayout user={user} setUser={setUser}><ProductionImprovements /></AppLayout></PrivateRoute>} />
                <Route path="/hr-improvements" element={<PrivateRoute><AppLayout user={user} setUser={setUser}><HRImprovements /></AppLayout></PrivateRoute>} />
                <Route path="/finance-improvements" element={<PrivateRoute><AppLayout user={user} setUser={setUser}><FinanceImprovements /></AppLayout></PrivateRoute>} />
                <Route path="/branch-improvements" element={<PrivateRoute><AppLayout user={user} setUser={setUser}><BranchImprovements /></AppLayout></PrivateRoute>} />
                <Route path="/system-improvements" element={<PrivateRoute><AppLayout user={user} setUser={setUser}><SystemImprovements /></AppLayout></PrivateRoute>} />

                {/* Advanced ERP Routes */}
                <Route path="/customers" element={<PrivateRoute><AppLayout user={user} setUser={setUser}><CustomerManagement /></AppLayout></PrivateRoute>} />
                <Route path="/pos" element={<PrivateRoute><AppLayout user={user} setUser={setUser}><POSManagement /></AppLayout></PrivateRoute>} />
                <Route path="/procurement" element={<PrivateRoute><AppLayout user={user} setUser={setUser}><ProcurementHub /></AppLayout></PrivateRoute>} />
                <Route path="/finance-advanced" element={<PrivateRoute><AppLayout user={user} setUser={setUser}><FinanceAdvanced /></AppLayout></PrivateRoute>} />
                <Route path="/warehouse" element={<PrivateRoute><AppLayout user={user} setUser={setUser}><WarehouseLogistics /></AppLayout></PrivateRoute>} />
                <Route path="/advanced-analytics" element={<PrivateRoute><AppLayout user={user} setUser={setUser}><AdvancedAnalytics /></AppLayout></PrivateRoute>} />
                <Route path="/assets" element={<PrivateRoute><AppLayout user={user} setUser={setUser}><AssetManagement /></AppLayout></PrivateRoute>} />
                <Route path="/store-operations" element={<PrivateRoute><AppLayout user={user} setUser={setUser}><StoreOperations /></AppLayout></PrivateRoute>} />

                <Route path="/hr" element={<PrivateRoute><HRAppLayout user={user} setUser={setUser}><HRDashboard /></HRAppLayout></PrivateRoute>} />
                <Route path="/hr/employees" element={<PrivateRoute><HRAppLayout user={user} setUser={setUser}><HREmployees /></HRAppLayout></PrivateRoute>} />
                <Route path="/hr/leave" element={<Navigate to="/hr/leave-management" replace />} />
                <Route path="/hr/payroll" element={<Navigate to="/hr/payroll-2026" replace />} />
                <Route path="/hr/payroll-approval" element={<PrivateRoute><HRAppLayout user={user} setUser={setUser}><PayrollApproval /></HRAppLayout></PrivateRoute>} />
                <Route path="/hr/payroll-2026" element={<PrivateRoute><HRAppLayout user={user} setUser={setUser}><Payroll2026 /></HRAppLayout></PrivateRoute>} />
                <Route path="/hr/departments" element={<PrivateRoute><HRAppLayout user={user} setUser={setUser}><HRDepartments /></HRAppLayout></PrivateRoute>} />
                <Route path="/hr/core-master-data" element={<PrivateRoute><HRAppLayout user={user} setUser={setUser}><HRCoreMasterData /></HRAppLayout></PrivateRoute>} />
                <Route path="/hr/onboarding" element={<PrivateRoute><HRAppLayout user={user} setUser={setUser}><EmployeeOnboarding /></HRAppLayout></PrivateRoute>} />
                <Route path="/hr/invitations" element={<PrivateRoute><HRAppLayout user={user} setUser={setUser}><EmployeeInvitations /></HRAppLayout></PrivateRoute>} />
                <Route path="/hr/employee-profile" element={<PrivateRoute><HRAppLayout user={user} setUser={setUser}><EmployeeProfile /></HRAppLayout></PrivateRoute>} />
                <Route path="/hr/performance" element={<PrivateRoute><HRAppLayout user={user} setUser={setUser}><HRPerformance /></HRAppLayout></PrivateRoute>} />
                <Route path="/hr/recruitment" element={<PrivateRoute><HRAppLayout user={user} setUser={setUser}><HRRecruitment /></HRAppLayout></PrivateRoute>} />
                <Route path="/hr/attendance" element={<PrivateRoute><HRAppLayout user={user} setUser={setUser}><HRAttendance /></HRAppLayout></PrivateRoute>} />
                <Route path="/hr/self-service" element={<Navigate to="/hr" replace />} />
                <Route path="/hr/reports" element={<Navigate to="/hr/analytics" replace />} />
                <Route path="/hr/attendance-reports" element={<PrivateRoute><HRAppLayout user={user} setUser={setUser}><HRAttendanceReports /></HRAppLayout></PrivateRoute>} />
                <Route path="/hr/org-chart" element={<PrivateRoute><HRAppLayout user={user} setUser={setUser}><OrgChart /></HRAppLayout></PrivateRoute>} />
                <Route path="/hr/time-attendance" element={<PrivateRoute><HRAppLayout user={user} setUser={setUser}><TimeAttendance /></HRAppLayout></PrivateRoute>} />
                <Route path="/hr/jobs" element={<PrivateRoute><HRAppLayout user={user} setUser={setUser}><JobPostings /></HRAppLayout></PrivateRoute>} />
                <Route path="/hr/leave-management" element={<PrivateRoute><HRAppLayout user={user} setUser={setUser}><LeaveManagement /></HRAppLayout></PrivateRoute>} />
                <Route path="/hr/my-payslips" element={<PrivateRoute><HRAppLayout user={user} setUser={setUser}><MyPayslips /></HRAppLayout></PrivateRoute>} />
                <Route path="/hr/payroll-management" element={<Navigate to="/hr/payroll-2026" replace />} />
                <Route path="/hr/performance-management" element={<PrivateRoute><HRAppLayout user={user} setUser={setUser}><PerformanceManagement /></HRAppLayout></PrivateRoute>} />
                <Route path="/hr/learning" element={<PrivateRoute><HRAppLayout user={user} setUser={setUser}><LearningDevelopment /></HRAppLayout></PrivateRoute>} />
                <Route path="/hr/engagement" element={<PrivateRoute><HRAppLayout user={user} setUser={setUser}><EmployeeEngagement /></HRAppLayout></PrivateRoute>} />
                <Route path="/hr/compliance" element={<PrivateRoute><HRAppLayout user={user} setUser={setUser}><Compliance /></HRAppLayout></PrivateRoute>} />
                <Route path="/hr/ai-insights" element={<PrivateRoute><HRAppLayout user={user} setUser={setUser}><AIInsights /></HRAppLayout></PrivateRoute>} />
                <Route path="/hr/settings" element={<PrivateRoute><HRAppLayout user={user} setUser={setUser}><HRSettings /></HRAppLayout></PrivateRoute>} />
                <Route path="/hr/comprehensive-report" element={<PrivateRoute><HRAppLayout user={user} setUser={setUser}><HRComprehensiveReport /></HRAppLayout></PrivateRoute>} />
                <Route path="/hr/analytics" element={<PrivateRoute><HRAppLayout user={user} setUser={setUser}><HRAnalyticsDashboard /></HRAppLayout></PrivateRoute>} />
                <Route path="/hr/audit-trail" element={<PrivateRoute><HRAppLayout user={user} setUser={setUser}><AuditTrail /></HRAppLayout></PrivateRoute>} />
                <Route path="/hr/bulk-operations" element={<PrivateRoute><HRAppLayout user={user} setUser={setUser}><BulkOperations /></HRAppLayout></PrivateRoute>} />
                <Route path="/admin/super" element={<PrivateRoute><HRAppLayout user={user} setUser={setUser}><SuperAdminDashboard /></HRAppLayout></PrivateRoute>} />
                <Route path="/hr/discipline" element={<PrivateRoute><HRAppLayout user={user} setUser={setUser}><HRDiscipline /></HRAppLayout></PrivateRoute>} />
                <Route path="/hr/discipline-grievance" element={<PrivateRoute><HRAppLayout user={user} setUser={setUser}><DisciplineGrievance /></HRAppLayout></PrivateRoute>} />
                <Route path="/hr/document-management" element={<PrivateRoute><HRAppLayout user={user} setUser={setUser}><HRDocumentManagement /></HRAppLayout></PrivateRoute>} />
                <Route path="/employee/my-documents" element={<PrivateRoute><HRAppLayout user={user} setUser={setUser}><MyDocuments /></HRAppLayout></PrivateRoute>} />

                <Route path="*" element={<NotFoundRedirect />} />
              </Routes>
              </ErrorBoundary>
            </Suspense>
          </BranchProvider>
        </SettingsProvider>
      </PermissionsProvider>
      </ConfirmDialogProvider>
      </I18nProvider>
    </BrowserRouter>
  );
}

export default App;
